"use client";

import { useState, useRef, useEffect, use } from "react";
import Link from "next/link";
import { Star, ChevronRight, ChevronLeft, Info } from "lucide-react";
import { useStoreDetail, useInfiniteStoreReviews } from "@/hooks/use-stores";
import { ReviewDetailModal } from "@/components/store/review-detail-modal";
import type { StoreReview, ReviewParams } from "@/types";
import { ulidFromSegment } from "@/lib/seo";

/* ─── Helpers ─── */

const AVATAR_COLORS = [
  "bg-green-600", "bg-violet-600", "bg-red-500", "bg-teal-600",
  "bg-blue-600", "bg-amber-500", "bg-rose-500", "bg-indigo-600",
  "bg-emerald-600", "bg-orange-500", "bg-cyan-600", "bg-pink-500",
];

function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatReviewDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return `Il y a ${diffDays} j`;
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} sem.`;
  if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
  return `Il y a ${Math.floor(diffDays / 365)} an${Math.floor(diffDays / 365) > 1 ? "s" : ""}`;
}


/* ─── Review card ─── */

function ReviewCard({ review, onClick }: { review: StoreReview; onClick: () => void }) {
  const initial = (review.customer.firstname?.[0] ?? "?").toUpperCase();
  const color = avatarColor(review.customer.firstname + review.customer.lastname);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border border-gray-200 dark:border-[#313131] p-5 space-y-3 transition hover:border-gray-300 dark:hover:border-[#444] hover:shadow-sm"
    >
      {/* Avatar + name row */}
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${color}`}>
          {initial}
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {review.customer.firstname} {review.customer.lastname?.slice(0, 1)}.
            </p>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {review.customer.reviews_count} Avis
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 shrink-0">
          <span>{formatReviewDate(review.created_at)}</span>
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
        </div>
      </div>

      {/* Stars + rating */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">{Number(review.rating).toFixed(1)}</span>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} fill="currentColor" className={`h-3.5 w-3.5 ${i < review.rating ? "text-yellow-400" : "text-gray-200 dark:text-gray-700"}`} />
          ))}
        </div>
      </div>

      {/* Comment (truncated) */}
      {review.comment && (
        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-400 line-clamp-3">{review.comment}</p>
      )}

      {/* Ordered items */}
      {review.order_items.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {review.order_items.slice(0, 3).map((item) => (
            <span key={item.ulid} className="rounded-full border border-gray-100 dark:border-[#313131] bg-gray-50 dark:bg-[#2A2A2A] px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              {item.name}
            </span>
          ))}
          {review.order_items.length > 3 && (
            <span className="rounded-full border border-gray-100 dark:border-[#313131] bg-gray-50 dark:bg-[#2A2A2A] px-2.5 py-1 text-[11px] font-semibold text-gray-400 dark:text-gray-500">
              +{review.order_items.length - 3}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

/* ─── Skeleton review card ─── */

function ReviewCardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-100 dark:border-[#313131] p-5 space-y-3 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gray-200 shrink-0" />
        <div className="space-y-1.5">
          <div className="h-3.5 w-28 rounded bg-gray-200" />
          <div className="h-3 w-16 rounded bg-gray-100 dark:bg-[#313131]" />
        </div>
      </div>
      <div className="h-3 w-36 rounded bg-gray-200" />
      <div className="space-y-1.5">
        <div className="h-3 w-full rounded bg-gray-100 dark:bg-[#313131]" />
        <div className="h-3 w-4/5 rounded bg-gray-100 dark:bg-[#313131]" />
        <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-[#313131]" />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════ */
/*  PAGE                                                    */
/* ════════════════════════════════════════════════════════ */

type SortOption = { label: string; sortBy: ReviewParams["sortBy"]; sortOrder: ReviewParams["sortOrder"] };

// Best-rated first by default — reviews lead with rating, not recency.
const SORT_OPTIONS: SortOption[] = [
  { label: "Meilleures notes", sortBy: "rating", sortOrder: "DESC" },
  { label: "Moins bonnes notes", sortBy: "rating", sortOrder: "ASC" },
  { label: "Les plus récents", sortBy: "created_at", sortOrder: "DESC" },
  { label: "Les plus anciens", sortBy: "created_at", sortOrder: "ASC" },
];

export default function StoreReviewsPage({
  params,
}: {
  params: Promise<{ storeId: string }>;
}) {
  const { storeId: segment } = use(params);
  const storeId = ulidFromSegment(segment) ?? segment;

  const [filterRating, setFilterRating] = useState<number | undefined>(undefined);
  const [sortIdx, setSortIdx] = useState(0);
  const [selectedReview, setSelectedReview] = useState<StoreReview | null>(null);
  const sort = SORT_OPTIONS[sortIdx];

  const { data: store, isLoading: storeLoading } = useStoreDetail(storeId);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteStoreReviews(storeId, {
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
    filterRating,
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (store?.name) document.title = `Avis — ${store.name} — Fast`;
  }, [store?.name]);

  // Pas de renvoi vers `/home` sur erreur : une relecture refusée (hors zone,
  // `/api/` interdit au rendu de Google) vidait la page de ses avis. Même cause
  // et même correction que sur la fiche — cf. store-view.tsx.

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const summary = data?.pages[0];
  const reviews = data?.pages.flatMap((p) => p.reviews) ?? [];
  const total = summary?.total_reviews ?? 0;

  return (
    <div className="min-h-screen bg-white dark:bg-[#1E1E1E] dark:bg-[#1E1E1E]">
      <div className="mx-auto px-4 lg:px-6 py-6">

        {/* ── Breadcrumb ── */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
          <Link href={`/stores/${segment}`} className="flex items-center gap-1.5 hover:text-gray-800 dark:text-gray-400 transition">
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            {storeLoading ? "…" : store?.name}
          </Link>
          <span>/</span>
        </div>

        {/* ── Page title ── */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Notes et avis</h1>

        {/* ── Two-column layout ── */}
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* ── LEFT: summary + filters (sticky) ── */}
          <div className="lg:w-[260px] shrink-0 lg:sticky lg:top-6 space-y-6">

            {/* Score */}
            {isLoading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-12 w-20 rounded bg-gray-200" />
                <div className="h-4 w-36 rounded bg-gray-100 dark:bg-[#313131]" />
              </div>
            ) : summary ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-5xl font-extrabold text-gray-900 dark:text-white leading-none">
                    {summary.average_rating.toFixed(1)}
                  </span>
                  <Star fill="currentColor" className="h-7 w-7 text-yellow-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                  {total >= 1000 ? `${(total / 1000).toFixed(0)}k+` : total}
                  {" "}notation{total !== 1 ? "s" : ""}, {total} avis public{total !== 1 ? "s" : ""}
                </p>
                <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition">
                  Qu&apos;est-ce qu&apos;un avis public&nbsp;?
                  <Info className="h-4 w-4 text-gray-400 dark:text-gray-500" strokeWidth={2} />
                </button>
              </div>
            ) : null}

            {/* Divider */}
            <div className="border-t border-gray-100 dark:border-[#313131]" />

            {/* Filter chips */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Filtrer</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterRating(undefined)}
                  className={`rounded-full border px-4 py-1.5 text-sm font-semibold text-left transition ${
                    filterRating === undefined
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-200 dark:border-[#313131] text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:bg-[#313131] dark:hover:bg-[#313131]"
                  }`}
                >
                  Tous
                </button>
                {[5, 4, 3, 2, 1].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterRating(filterRating === s ? undefined : s)}
                    className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                      filterRating === s
                        ? "border-gray-900 bg-gray-900 text-white"
                        : "border-gray-200 dark:border-[#313131] text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:bg-[#313131] dark:hover:bg-[#313131]"
                    }`}
                  >
                    {s} <Star fill="currentColor" className={`h-3.5 w-3.5 ${filterRating === s ? "text-yellow-300" : "text-yellow-400"}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Trier</p>
              <select
                value={sortIdx}
                onChange={(e) => setSortIdx(Number(e.target.value))}
                className="w-full rounded-xl border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 focus:outline-none focus:border-gray-400"
              >
                {SORT_OPTIONS.map((opt, i) => (
                  <option key={i} value={i}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── RIGHT: review list ── */}
          <div className="flex-1 min-w-0">
            {isLoading && (
              <div className="grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => <ReviewCardSkeleton key={i} />)}
              </div>
            )}

            {!isLoading && reviews.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-gray-400 dark:text-gray-500 text-sm">Aucun avis pour cette sélection.</p>
              </div>
            )}

            {reviews.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                {reviews.map((review) => (
                  <ReviewCard key={review.ulid} review={review} onClick={() => setSelectedReview(review)} />
                ))}
              </div>
            )}

            <div ref={sentinelRef} className="h-4" />

            {isFetchingNextPage && (
              <div className="flex justify-center py-6">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-900 border-t-transparent" />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedReview && (
        <ReviewDetailModal review={selectedReview} onClose={() => setSelectedReview(null)} />
      )}
    </div>
  );
}
